import { canv } from "@/canv/global.js";
import { saveTextToClipboard } from "@/canvasController/helpers";
import { elementBase } from "@/elementBase/elementBase";
import { CommentWidget } from "@/elements/comment/index.js";
import { frameWidget } from "@/elements/frame";
import { mindMapWidget } from "@/elements/mindMap/mindMap.js";
import { PresentationWidget } from "@/elements/Presentation/Presentation";
import { eventEmitter } from "@/eventEmitter";
import { Group } from "@/group";
import { lockElementAndDescendants } from "@/interface/WidgetMenu/lib/lockElementAndDescendants";
import { selectionManager } from "@/tools/cursorTool";

const TEXT_EDIT_COMMANDS = ["copy", "paste", "delete"];

export class ContextMenuController {
    constructor() {
        this.isExporting = false;
        eventEmitter.on("button.disabled", (buttonStates) => {
            this.isExporting = buttonStates.export;
        });
    }

    isOpened = false;
    isExporting = false;

    hide() {
        eventEmitter.dispatch("contextMenu.visibility", { isVisible: false, anchor: undefined });
        this.isOpened = false;
    }

    show() {
        if (
            canv.isBoardModeView() ||
            canv.canvasController.selected[0]?.children[0] instanceof CommentWidget
        ) {
            return;
        }

        if (canv.canvasController.selected.length !== 0) {
            this.buildSelectedContextMenuContent();
        } else {
            this.buildCanvasContextMenuContent();
        }

        eventEmitter.dispatch("contextMenu.visibility", { isVisible: true });
        this.isOpened = true;
    }

    buildSelectedContextMenuContent() {
        const menuFlags = {
            copy: true,
            paste: true,
            duplicate: false, //только для презентации
            lock: false, //только для презентации
            unlock: false, //только для презентации
            comment: false,
            toFront: false,
            toBack: false,
            arrange: false,
            redo: false,
            undo: false,
            copyAsPng: false,
            export: false,
            linkToWidget: false,
            delete: true,
            addSticker: false,
            addText: false,
            addShape: false,
        };

        const isOnlyPresentation = (groups: Group[]) => {
            if (groups.length === 1) {
                return (
                    (groups[0].isItemWrapper() &&
                        groups[0].children[0] instanceof PresentationWidget) ||
                    (!groups[0].isItemWrapper() &&
                        groups[0].isLocked &&
                        groups[0].children.length === 1 &&
                        (groups[0].children[0] as Group).children[0] instanceof PresentationWidget)
                );
            }

            return false;
        };

        if (isOnlyPresentation(canv.canvasController.selected)) {
            menuFlags.duplicate = true;
            if (canv.canvasController.selected[0].isLocked) {
                menuFlags.unlock = true;
            } else {
                menuFlags.lock = true;
            }
            menuFlags.redo = true;
            menuFlags.undo = true;
            menuFlags.export = true;
            menuFlags.linkToWidget = true;

            eventEmitter.dispatch("contextMenu.visibilityMenuItems", {
                menuItems: menuFlags,
            });

            return;
        }

        const isOnlyEmbed = (elements) => {
            return elements.every((g) => {
                if (g.children?.[0] instanceof Group) {
                    return isOnlyEmbed(g.children);
                }

                return g.children.every((el) => el.constructorName === "embed");
            });
        };
        const selectOnlyEmbed = isOnlyEmbed(canv.canvasController.selected);
        // выбрана только майндмапа не в режиме редактирования текста
        const selected = canv.canvasController.selected;
        const cursorTool = canv.toolBox.TryGetToolByName("cursorTool");
        let isOnlyMindMap = false;

        if (cursorTool.isMode("interaction") && selected.length === 1) {
            const child = selected[0].onlyChild();
            if (child instanceof mindMapWidget) {
                isOnlyMindMap = child.interactionType === "select";
            }
        }

        if (!canv.toolBox.TryGetToolByName("cursorTool").isMode("interaction") || isOnlyMindMap) {
            menuFlags.comment = true;
            if (!selectOnlyEmbed) {
                menuFlags.toFront = true;
                menuFlags.toBack = true;
            }
            menuFlags.arrange = true;
            menuFlags.redo = true;
            menuFlags.undo = true;
            if (!selectOnlyEmbed) {
                menuFlags.copyAsPng = true;
                if (!canv.isBoardModeViewWithMini() && !this.isExporting) {
                    menuFlags.export = true;
                }
            }
            if (canv.canvasController.selected.length === 1) {
                menuFlags.linkToWidget = true;
            }
        }

        eventEmitter.dispatch("contextMenu.visibilityMenuItems", {
            menuItems: menuFlags,
        });
    }

    buildCanvasContextMenuContent() {
        eventEmitter.dispatch("contextMenu.visibilityMenuItems", {
            menuItems: {
                copy: false,
                paste: true,
                duplicate: false, //только для презентации
                lock: false, //только для презентации
                unlock: false, //только для презентации
                comment: true,
                toFront: false,
                toBack: false,
                arrange: false,
                redo: false,
                undo: false,
                copyAsPng: false,
                export: false,
                linkToWidget: false,
                delete: false,
                addSticker: true,
                addText: true,
                addShape: true,
            },
        });
    }

    async callProcessorByCodeName(codeName) {
        if (TEXT_EDIT_COMMANDS.includes(codeName) && canv.textEditingController.getIsEditing()) {
            await canv.textEditingController.handleContextMenuEditCommand(codeName);
            return;
        }

        if (codeName === "cut") {
            const commandMem = canv.keyboard.command;
            canv.keyboard.command = "ctrl+x";
            canv.toolBox.TryGetToolByName("cursorTool").TryAction("keyboardDown");
            canv.keyboard.command = commandMem;
        }
        if (codeName === "copy") {
            const commandMem = canv.keyboard.command;
            canv.keyboard.command = "ctrl+c";
            canv.toolBox.TryGetToolByName("cursorTool").TryAction("keyboardDown");
            canv.keyboard.command = commandMem;
        } else if (codeName === "paste") {
            const commandMem = canv.keyboard.command;
            canv.keyboard.command = "ctrl+v";
            canv.toolBox.TryGetToolByName("cursorTool").TryAction("keyboardDown");

            const done = new Promise<void>((resolve) => {
                const handler = () => {
                    eventEmitter.off("paste.done", handler);
                    resolve();
                };
                eventEmitter.on("paste.done", handler);
            });

            await done;
            canv.keyboard.command = commandMem;
        } else if (codeName === "comment") {
            const commentTool = canv.toolBox.TryGetToolByName("commentTool");
            commentTool.createComment();
            canv.redraw();
        } else if (codeName === "toFront") {
            canv.canvasController.toProccessor("front");
            canv.redraw();
        } else if (codeName === "toBack") {
            canv.canvasController.toProccessor("back");
            canv.redraw();
        } else if (codeName === "addSticker") {
            canv.toolBox.TryGetToolByName("stickerTool").createSticker();
            canv.redraw();
        } else if (codeName === "addText") {
            canv.toolBox.TryGetToolByName("textTool").createText();
            canv.redraw();
        } else if (codeName === "delete") {
            const commandMem = canv.keyboard.command;
            canv.keyboard.command = "Backspace";
            canv.toolBox.TryGetToolByName("cursorTool").TryAction("keyboardDown");
            canv.keyboard.command = commandMem;
        } else if (codeName === "addShape") {
            canv.toolBox.TryGetToolByName("shapeTool").createShape();
            canv.redraw();
        } else if (codeName === "copyAsPng") {
            this.exportAs("png");
        } else if (codeName === "export") {
            canv.appInterface.exportWindow.open();
        } else if (codeName === "linkToWidget") {
            await this.linkToWidget();
        } else if (codeName === "undo") {
            canv.eventManager.Undo();
        } else if (codeName === "redo") {
            canv.eventManager.Redo();
        } else if (codeName === "alignLeft") {
            canv.canvasController.alignSelected("left");
        } else if (codeName === "alignHCenters") {
            canv.canvasController.alignSelected("hCenters");
        } else if (codeName === "alignRight") {
            canv.canvasController.alignSelected("right");
        } else if (codeName === "alignTop") {
            canv.canvasController.alignSelected("top");
        } else if (codeName === "alignVCenters") {
            canv.canvasController.alignSelected("vCenters");
        } else if (codeName === "alignBottom") {
            canv.canvasController.alignSelected("bottom");
        } else if (codeName === "lock") {
            if (canv.canvasController.selected.length === 0) return;
            canv.eventManager.openRecord();
            const [newGroup] = canv.canvasController.TryGroup(
                canv.canvasController.selected,
                true,
                canv.canvasController.selected.length === 1
                    ? canv.canvasController.selected[0].indexByParent
                    : null,
            );
            lockElementAndDescendants(newGroup, true);
            selectionManager.setSelected([newGroup]);
            canv.eventManager.closeRecord();
        } else if (codeName === "unlock") {
            if (canv.canvasController.selected.length !== 1) return;
            canv.eventManager.openRecord();

            const selectedGroup = canv.canvasController.selected[0];

            lockElementAndDescendants(selectedGroup, false);

            if (selectedGroup.parent) {
                // если мы выбрали элемент внутри группы и хотим его разлочить - нужно вначале разгруппировать
                // при выделении залоченой группы у нас выделяется группа и у нее есть parent - lock группа
                canv.canvasController.TryUngroup(selectedGroup.parent);
            } else if (selectedGroup.onlyChild() instanceof elementBase) {
                selectedGroup.fixState("lock");
                selectedGroup.isLocked = false;
                selectedGroup.saveState();

                selectionManager.setSelected([selectedGroup]);
            } else {
                const childGroups = [...selectedGroup.children];

                for (let i = 0; i < childGroups.length; i++) {
                    childGroups[i].fixState("parentUID");
                    childGroups[i].setParent(null);
                    childGroups[i].saveState();
                }

                selectedGroup.children = [];

                canv.eventManager.createBuildEvent("delete", selectedGroup);

                canv.canvasController.groups.delete(selectedGroup.UID);
                selectionManager.setSelected(childGroups as Group[]);
            }
            canv.eventManager.closeRecord();
        } else if (codeName === "duplicate") {
            canv.canvasController.duplicateProccessor();
        }
    }

    exportAs(mode = "png") {
        const savedTransformation = {
            0: canv.m[0],
            3: canv.m[3],
            4: canv.m[4],
            5: canv.m[5],
        };
        canv.multiManager.changeVisibilityCursor(false);
        canv.zoomToSelected();

        // готовим props
        const props = canv.canvasController.getSelectedProps();
        const selectedGroups = canv.canvasController.getSelected();
        selectionManager.clearSelected();

        // create canvas
        const newCanvas = document.createElement("canvas");
        newCanvas.classList.add("exportCanvas");
        document.body.prepend(newCanvas);

        // setup canvas
        const visualWidth = (props.width / canv.dpr) * canv.m[0];
        const visualHeight = (props.height / canv.dpr) * canv.m[0];

        newCanvas.style.width = visualWidth.toString() + "px";
        newCanvas.style.height = visualHeight.toString() + "px";

        const rect = newCanvas.getBoundingClientRect();
        newCanvas.width = rect.width * canv.dpr;
        newCanvas.height = rect.height * canv.dpr;

        const newCtx = newCanvas.getContext("2d");
        if (!newCtx) {
            return;
        }
        const appCtx = canv.ctx;

        canv.ctx = newCtx;

        canv.ctx.setTransform(
            canv.m[0],
            0,
            0,
            canv.m[3],
            -props.startX * canv.m[0],
            -props.startY * canv.m[0],
        );

        const groupsForExport: Group[] = [];
        selectedGroups.forEach((group) => {
            groupsForExport.push(group);

            group.children.forEach((child) => {
                // по требованию экспортируем все содержимое frameWidget даже не выделенное
                if (child instanceof frameWidget) {
                    groupsForExport.push(...child.getOverlappedGroups());
                }
            });
        });

        // рисуем на новом canvas
        canv.redraw();
        canv.redraw(true, true, groupsForExport);

        const isChromium = !!window.chrome;

        if (mode === "png") {
            if (isChromium) {
                this.copyAsPngChomium(newCanvas);
            } else {
                this.copyAsPngW(newCanvas);
            }
        }
        canv.multiManager.changeVisibilityCursor(true);

        // возвращаем все на место
        canv.ctx = appCtx;
        selectionManager.setSelected(selectedGroups);

        canv.m[0] = savedTransformation["0"];
        canv.m[3] = savedTransformation["3"];
        canv.m[4] = savedTransformation["4"];
        canv.m[5] = savedTransformation["5"];
        canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
    }

    copyAsPngW(newCanvas) {
        if (
            typeof ClipboardItem === "undefined" ||
            !navigator.clipboard ||
            !navigator.clipboard.write
        ) {
            return;
        }
        const makeImagePromise = async (): Promise<Blob> => {
            if (navigator.permissions?.query) {
                try {
                    await navigator.permissions.query({
                        name: "clipboard-write" as PermissionName,
                    });
                } catch {
                    throw new Error("Clipboard нет прав на запись");
                }
            }

            const snapshot = await new Promise<Blob>((resolve, reject) => {
                newCanvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            reject(new Error("Failed to create blob"));
                            return;
                        }
                        resolve(blob);
                    },
                    "image/png",
                    1,
                );
            });

            newCanvas.remove();
            return snapshot;
        };

        navigator.clipboard.write([
            new ClipboardItem({
                "image/png": makeImagePromise(),
            }),
        ]);
    }

    async copyAsPngChomium(newCanvas) {
        const blob = await new Promise((resolve) => newCanvas.toBlob(resolve, "image/png", 1));

        newCanvas.outerHTML = "";

        if (navigator.permissions !== undefined && navigator.permissions.query !== undefined) {
            try {
                const permission = await navigator.permissions.query(<PermissionDescriptor>{
                    name: "clipboard-write" as PermissionName,
                });
            } catch (_) {
                return;
            }
        }

        navigator.clipboard.write([
            new ClipboardItem({
                "image/png": blob as Blob,
            }),
        ]);
    }

    async linkToWidget() {
        const pathToGroup = `${canv.host}/board/${canv.getInitDataProp("boardUID")}?linkToWidget=${canv.canvasController.selected[0].UID}`;
        saveTextToClipboard(pathToGroup);
    }
}
